import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { flushSync } from 'react-dom';
import { TranslationService } from '../../../../services/translation.service';
import TextFieldFormGroup from '../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../validation/validation-info';
import ValidationSummary from '../../../Shared/ValidationSummary';
import { ValidatableComponent, ValidationHelper } from '../../../../helpers/validation.helper';
import type IValidatable from '../../../Shared/validatable.interface';
import FileUpload, { type IFileUploadState } from '../../../Shared/FileUpload';
import HoneyPot, { type IHoneyPot } from '../../../Shared/HoneyPot';
import Consent, { type IConsentState } from '../../../Shared/Consent';
import ContactInformationSimple, { type IContactInformationSimpleState } from '../../../Shared/ContactInformationSimple';
import { ContactInformationSimpleField } from '../../../Shared/ContactInformationSimple/contact-information-simple-field.enum';
import FormSubmitResult from '../FormSubmitResult';
import ToggleablePanel from '../../../Shared/ToggleablePanel';
import ActualTripSelector, { type IActualTripSelectorState } from '../../../Shared/ActualTripSelector';
import FeedbackRequested from '../FeedbackRequested';
import { AnalyticsService } from '../../../../services/analytics-service';
import { LanguageService } from '../../../../services/language.service';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import type { IWishApplication } from './models/wish-application.model';
import { DateHelper } from '../../../../helpers/date.helper';
import { Status } from '../../../../hooks/fetch.hook';
import { ActualTripSelectorMode } from '../../../Shared/ActualTripSelector/actual-trip-selector-mode.enum';
import { ActualTripSelectorField } from '../../../Shared/ActualTripSelector/actual-trip-selector-field.enum';
import AntiforgeryService from '../../../../services/anti-forgery.service';

function WishForm({ onCompletedChange }: { onCompletedChange: (completed: boolean) => void }) {
  // === STATE ===
  const [descriptionState, setDescriptionState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [showActualTrip, setShowActualTrip] = useState(false);
  const [actualTripSelectorState, setActualTripSelectorState] = useState<IActualTripSelectorState>({
    value: {
      fromLocation: undefined,
      fromDateTime: DateHelper.roundToNearestFiveMinutes(DateTime.now().minus({ hours: 2 })),
      toLocation: undefined,
      toDateTime: DateHelper.roundToNearestFiveMinutes(DateTime.now().minus({ hours: 2 })),
      comments: '',
      mode: ActualTripSelectorMode.edit,
    },
    validationInfo: new ValidationInfo(),
  });
  const [fileUploadState, setFileUploadState] = useState<IFileUploadState>({ value: [], validationInfo: new ValidationInfo() });
  const [feedbackRequested, setFeedbackRequested] = useState(false);
  const [contactInformationState, setContactInformationState] = useState<IContactInformationSimpleState>({
    value: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
    },
    validationInfo: new ValidationInfo(),
  });
  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });
  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [submitErrorCode, setSubmitErrorCode] = useState<number | undefined>(undefined);

  // === REFS ===
  const descriptionRef = useRef<IValidatable>(null!);
  const fileUploadRef = useRef<IValidatable>(null!);
  const contactInformationRef = useRef<IValidatable>(null!);
  const consentRef = useRef<IValidatable>(null!);
  const honeyPotRef = useRef<IHoneyPot>(null!);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: descriptionRef.current, validationInfo: descriptionState.validationInfo, setState: setDescriptionState },
      { component: fileUploadRef.current, validationInfo: fileUploadState.validationInfo, setState: setFileUploadState },
      { component: contactInformationRef.current, validationInfo: contactInformationState.validationInfo, setState: setContactInformationState },
      { component: consentRef.current, validationInfo: consentState.validationInfo, setState: setConsentState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.QuestionsAndOpinions, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;
      setSubmitStatus(Status.notperformed);
      setSubmitErrorCode(undefined);

      if (!honeyPotRef.current.isValid()) return;
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.QuestionsAndOpinions, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      setSubmitStatus(Status.loading);

      const application: IWishApplication = {
        freeText: descriptionState.value,
        isRelatedToJourney: showActualTrip,
        actualTripFromGid: actualTripSelectorState.value.fromLocation?.location?.gid,
        actualTripFromName: actualTripSelectorState.value.fromLocation?.location?.name,
        actualTripFromDateTime: actualTripSelectorState.value.fromDateTime,
        actualTripToGid: actualTripSelectorState.value.toLocation?.location?.gid,
        actualTripToName: actualTripSelectorState.value.toLocation?.location?.name,
        actualTripToDateTime: actualTripSelectorState.value.toDateTime,
        isFeedbackRequested: feedbackRequested,
        firstName: contactInformationState.value.firstName,
        lastName: contactInformationState.value.lastName,
        phoneNumber: contactInformationState.value.phoneNumber,
        email: contactInformationState.value.email,
        isConsentGiven: consentState.value,
      };
      const formData = new FormData();
      formData.append('wishInformation', JSON.stringify(application));
      fileUploadState.value.forEach((file, index) => {
        formData.append(`files[${index}]`, file.actualFile);
      });
      try {
        const response = await fetch(`/api/questionsandopinions/wish?language=${LanguageService.getCurrentLanguage()}`, {
          method: 'POST',
          body: formData,
          headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        });
        if (response.ok) {
          setSubmitStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          onCompletedChange(true);
          AnalyticsService.addEvent(AnalyticsService.QuestionsAndOpinions, 'Skickat önskemål', '');
        } else {
          if (response.status === 400) {
            const responseBody = await response.json();
            if (!responseBody.errors) throw new Error('Missing errors');
            handleServerValidationErrors(responseBody.errors);
          }
          setSubmitStatus(Status.failed);
          setSubmitErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          AnalyticsService.addEvent(AnalyticsService.QuestionsAndOpinions, 'Skickat önskemål - misslyckat', '');
        }
      } catch {
        setSubmitStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: undefined };
        onCompletedChange(true);
      }
    });
    setTimeout(() => {
      document.getElementById('questionsandopinions')?.scrollIntoView(true);
      document.getElementById(!isFormValid || submitResult?.errorCode === 400 ? 'wish-form-validation-summary' : 'wish-form-submit-result')?.focus();
    }, 100);
  }

  return (
    <div id="wish-form" className="wish-form">
      <div hidden={!(submitStatus !== Status.success && (submitStatus !== Status.failed || submitErrorCode === 400))}>
        <h2 className="d-block d-md-none">{TranslationService.get('questionsandopinionspage.wish.heading')}</h2>
        {TranslationService.get('common.formsrequiredfieldsmessage') && <p className="mb-3">{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
        <ValidationSummary id="wish-form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
        <TextFieldFormGroup
          ref={descriptionRef}
          required={true}
          renderAsTextArea={true}
          maxLength={2000}
          labelText={TranslationService.get('questionsandopinions.wish.subheading')}
          rows={5}
          inputId={'wish-description'}
          serverValidationName="freeText"
          state={descriptionState}
          onChange={(value) => {
            setDescriptionState((ps) => ({ ...ps, value }));
          }}
          onKeyUp={() => {
            setDescriptionState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }));
          }}
        />
        <hr className="my-4" />
        <ToggleablePanel id="wish-toggleable-panel" panelVisible={showActualTrip} toggleText={TranslationService.get('questionsandopinions.wish.fromtoheading')} onToggleChange={setShowActualTrip}>
          <ActualTripSelector
            id="wish-actual-trip-selector"
            minDateTime={DateTime.now().startOf('day').minus({ days: 60 })}
            maxDateTime={DateTime.now().endOf('day').plus({ days: 60 })}
            showComments={false}
            state={actualTripSelectorState}
            onChange={(value) => setActualTripSelectorState((ps) => ({ ...ps, value }))}
            onKeyUp={(e) => {
              if (e === ActualTripSelectorField.fromLocation) {
                setActualTripSelectorState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, fromLocation: new ValidationInfo() }),
                }));
              } else if (e === ActualTripSelectorField.fromDateTime) {
                setActualTripSelectorState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, fromDateTime: new ValidationInfo() }),
                }));
              } else if (e === ActualTripSelectorField.toLocation) {
                setActualTripSelectorState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, toLocation: new ValidationInfo() }),
                }));
              } else if (e === ActualTripSelectorField.toDateTime) {
                setActualTripSelectorState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, toDateTime: new ValidationInfo() }),
                }));
              } else if (e === ActualTripSelectorField.comments) {
                setActualTripSelectorState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, comments: new ValidationInfo() }),
                }));
              }
            }}
          />
        </ToggleablePanel>
        <hr className="my-4" />
        <FileUpload
          id="wish-file-upload"
          ref={fileUploadRef}
          state={fileUploadState}
          onFilesAdded={(files) => {
            setFileUploadState((ps) => ({ ...ps, value: [...ps.value, ...files] }));
          }}
          onFileRemoved={(file) => {
            setFileUploadState({ ...fileUploadState, value: fileUploadState.value.filter((f) => f !== file), validationInfo: new ValidationInfo() });
          }}
        />
        <hr className="my-4" />
        <FeedbackRequested id="wish-feedback-requested" feedbackRequested={feedbackRequested} onFeedbackRequestedChange={setFeedbackRequested} className="mb-3" />
        <ContactInformationSimple
          id="wish-contact-information"
          ref={contactInformationRef}
          firstNameRequired={feedbackRequested}
          emailRequired={feedbackRequested}
          className="mb-4"
          state={contactInformationState}
          onChange={(value) => {
            setContactInformationState((ps) => ({ ...ps, value }));
          }}
          onKeyUp={(e) => {
            if (e === ContactInformationSimpleField.firstName) {
              setContactInformationState((ps) => ({
                ...ps,
                validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                  ...ps.validationInfo.children,
                  firstName: new ValidationInfo(),
                }),
              }));
            } else if (e === ContactInformationSimpleField.lastName) {
              setContactInformationState((ps) => ({
                ...ps,
                validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                  ...ps.validationInfo.children,
                  lastName: new ValidationInfo(),
                }),
              }));
            } else if (e === ContactInformationSimpleField.phoneNumber) {
              setContactInformationState((ps) => ({
                ...ps,
                validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                  ...ps.validationInfo.children,
                  phoneNumber: new ValidationInfo(),
                }),
              }));
            } else if (e === ContactInformationSimpleField.email) {
              setContactInformationState((ps) => ({
                ...ps,
                validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                  ...ps.validationInfo.children,
                  email: new ValidationInfo(),
                }),
              }));
            }
          }}
        />
        <Consent
          id="wish-consent"
          ref={consentRef}
          consentText={TranslationService.get('questionsandopinions.agreementcheckboxtext')}
          integrityPolicyInfo={TranslationService.get('questionsandopinions.agreementinfolinktext')}
          termsAndConditions={TranslationService.get('customerserviceforms.termsandconditions.heading')}
          className="mb-4"
          state={consentState}
          onChange={(value) => {
            setConsentState({ value, validationInfo: new ValidationInfo() });
          }}
        />
        <HoneyPot ref={honeyPotRef} />
        <div className="d-grid">
          <button type="button" className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick}>
            <span hidden={submitStatus === Status.loading}>{TranslationService.get('questionsandopinionspage.wish.submit')}</span>
          </button>
        </div>
      </div>
      <FormSubmitResult
        id="wish-form-submit-result"
        submitStatus={submitStatus}
        submitErrorCode={submitErrorCode}
        successText={TranslationService.get('questionsandopinions.wish.thankyoutext')}
        failedText={TranslationService.get('questionsandopinions.failedsubmissiontext')}
        dismissButtonText={TranslationService.get('questionsandopinions.dismissfailedsubmissiontext')}
        onDismiss={() => {
          setSubmitStatus(Status.notperformed);
          onCompletedChange(false);
        }}
      />
    </div>
  );
}

export default WishForm;
